import { useEffect, useRef, useState } from 'react'

import { PROFILE_ROUTE_NAMES } from '../../../../constants/route'
import { ScreenContainerRef } from '../../../../context'
import { useOutboundUuid } from '../../../../hooks'
import {
  EmailProfileActionsContextProvider,
  EmailProfileContextProvider,
  EmailProfileMessagesListExpandContextProvider,
} from '../../../Email'
import { MINIMIZED_MODAL_SCOPE } from '../../../Modal'
import { MultipleViewTypeListActiveItemPreviewCloseButton } from '../../../MultipleViewTypeList'
import {
  getRequestThreadMessagePrintPageLink,
  getRequestThreadMessageRawPageLink,
} from '../../../Request'
import {
  RequestProfileTabEmailsThreadModalActionsHeader,
  RequestProfileTabEmailsThreadModalBody,
  RequestProfileTabEmailsThreadModalHeader,
  useRequestProfileTabEmailsThreadModalHeaderActions,
  useRequestProfileTabEmailsThreadModalLoaders,
  useRequestProfileTabEmailsThreadModalMessageHandlers,
  useRequestProfileTabEmailsThreadModalMessageItemProps,
  useRequestProfileTabEmailsThreadModalSummaryProps,
} from '../RequestProfileTabEmailsThreadModal'

import * as styles from './RequestProfileTabEmailsThreadPreview.module.css'

function RequestProfileTabEmailsThreadPreview({
  activeItem: mailUuid,
  audience,
  currentIndex,
  directoryId,
  hasNext,
  hasPrevious,
  profileLoadersRef,
  requestId,
  totalCount,
  onClosePreview,
  onNext,
  onPrevious,
  onReleaseActiveItem,
  onReloadList,
  onSuccessRead,
}) {
  const outboundUuid = useOutboundUuid()

  const [modal, setModal] = useState(null)
  const [isPerformingMessageAction, setPerformingMessageAction] =
    useState(false)

  const scrollWrapperRef = useRef(null)

  const profileLoaders = useRequestProfileTabEmailsThreadModalLoaders({
    mailUuid,
    requestId,
    onSuccessRead,
  })

  const {
    entityData,
    getMessageThresholdItemsProps,
    handleReloadProfile,
    handleUpdateEntity,
    isInitiated,
    isReloadingThread,
    loadEmailMessages,
    loadingMoreMessage,
    messages,
  } = profileLoaders

  useEffect(() => {
    if (profileLoadersRef) {
      profileLoadersRef.current = profileLoaders
    }
  }, [profileLoaders])

  const isReadyToShowMessages =
    isInitiated && messages !== null && !isReloadingThread

  const isThreadUnavailable = isReadyToShowMessages && messages.length === 0

  const { handleClickAction, iconBarActions, isPerformingAction, menuActions } =
    useRequestProfileTabEmailsThreadModalHeaderActions({
      directoryId,
      entityData,
      mailUuid,
      messages,
      minimizedModalScope: MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE,
      requestId,
      setModal,
      onClose: onReleaseActiveItem,
      onReloadList,
      onReloadProfile: handleReloadProfile,
      onThreadRemoved: onReleaseActiveItem,
    })

  function handleCloseNestedModal() {
    setModal(null)
  }

  const { handleMarkAsUnread, handleSuccessReply, updateMessage } =
    useRequestProfileTabEmailsThreadModalMessageHandlers({
      mailUuid,
      onReloadList,
      onReloadProfile: handleReloadProfile,
    })

  const messageItemProps =
    useRequestProfileTabEmailsThreadModalMessageItemProps({
      audience,
      getPrintPageLink: ({ additionalParams, id }) =>
        getRequestThreadMessagePrintPageLink({
          additionalParams,
          id,
          outboundUuid,
          requestId,
        }),
      getRawPageLink: ({ additionalParams, id }) =>
        getRequestThreadMessageRawPageLink({
          additionalParams,
          id,
          outboundUuid,
          requestId,
        }),
      requestId,
      onReloadList,
      onSuccessReply: handleSuccessReply,
      onThreadRemoved: onReleaseActiveItem,
    })

  const summaryProps = useRequestProfileTabEmailsThreadModalSummaryProps({
    entityData,
    mailUuid,
    onReloadProfile: handleReloadProfile,
  })

  return (
    <EmailProfileContextProvider
      entityData={entityData}
      handleCloseModal={handleCloseNestedModal}
      handleReloadEntity={handleReloadProfile}
      handleReloadMessages={loadEmailMessages}
      handleUpdateEntity={handleUpdateEntity}
      handleUpdateMessage={updateMessage}
      isInitiated={isReadyToShowMessages}
      mailUuid={mailUuid}
      messages={messages}
      setModal={setModal}
      setPerformingAction={setPerformingMessageAction}
      sub={null}
    >
      <EmailProfileActionsContextProvider
        getPrintPageLink={PROFILE_ROUTE_NAMES.emailThreadPrint}
        onUnread={handleMarkAsUnread}
      >
        <EmailProfileMessagesListExpandContextProvider messages={messages}>
          <ScreenContainerRef value={scrollWrapperRef}>
            <div className={styles.RequestProfileTabEmailsThreadPreview}>
              <RequestProfileTabEmailsThreadModalHeader
                isSkeleton={!isReadyToShowMessages}
                mailUuid={mailUuid}
                persistentActions={
                  <MultipleViewTypeListActiveItemPreviewCloseButton
                    onClose={onClosePreview}
                  />
                }
                requestId={requestId}
                subject={entityData?.subject}
              />
              <RequestProfileTabEmailsThreadModalActionsHeader
                actionsDisabled={
                  isThreadUnavailable ||
                  isPerformingAction ||
                  isPerformingMessageAction
                }
                currentIndex={currentIndex}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                iconBarActions={iconBarActions}
                isNavigationDisabled={
                  isPerformingAction || isPerformingMessageAction
                }
                isReadyToShowMessage={isReadyToShowMessages}
                menuActions={menuActions}
                totalCount={totalCount}
                onClickAction={handleClickAction}
                onNext={onNext}
                onPrevious={onPrevious}
              />
              <RequestProfileTabEmailsThreadModalBody
                containerRef={scrollWrapperRef}
                getMessageThresholdItemsProps={getMessageThresholdItemsProps}
                isInitiated={isInitiated}
                isReloadingThread={isReloadingThread}
                itemProps={messageItemProps}
                loadingMore={loadingMoreMessage}
                mailUuid={mailUuid}
                messages={messages}
                summaryProps={summaryProps}
              />
            </div>
            {modal}
          </ScreenContainerRef>
        </EmailProfileMessagesListExpandContextProvider>
      </EmailProfileActionsContextProvider>
    </EmailProfileContextProvider>
  )
}

export default RequestProfileTabEmailsThreadPreview
